import { FLAG_COLOURS, NEUTRAL_COLOURS } from 'constants/colors'
import { Option, Signature } from 'constants/models'
import { AllergyTypes, Child, FamilyMaritalStatus, FamilyPosition, Gender } from 'services/child/constants'
import { Carer } from 'services/legacy/carers/models'

import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'

import i18n from 'translations'

export enum RegistrationFormStatus {
  COMPLETED = 'completed',
  LINK_EXPIRED = 'link_expired',
  NOT_SENT = 'not_sent',
  SENT = 'sent',
}

export enum RegistrationFormObjectAction {
  DELETE = 'delete',
}

export interface FormPreviewCarerChildRelation {
  carer?: Omit<Carer, 'title' | 'photo'> & {
    objectId?: number
    photo?: string | SelectedImage
    title?: Option<string>
  }
  objectAction?: RegistrationFormObjectAction
  objectId?: number
  relation?: Option<string>
}

export type FormPreviewDataFormValues = Omit<
  Child,
  'ethnicity' | 'carerChildRelations' | 'information' | 'profile' | 'gender' | 'religion'
> & {
  carerChildRelations?: FormPreviewCarerChildRelation[]
  ethnicity?: Option<number>
  gender?: Option<Gender>
  information?: Omit<
    NonNullable<Child['information']>,
    'allergies'
    | 'applyPermissions'
    | 'travelPermissions'
    | 'daytimeSleepingBehavior'
    | 'dietaryReq'
  > & {
    allergies?: Option<AllergyTypes> | AllergyTypes
    applyPermissions?: {
      facePaint?: boolean
      nappyCream?: boolean
      patches?: boolean
      sunCream?: boolean
    }
    daytimeSleepingBehavior?: boolean
    daytimeSleepingBehaviorDetails?: string
    dietaryReq?: Option<string>
    travelPermissions?: {
      canGoToExternalOutgoings?: boolean
      canTakeTheBus?: boolean
    }
  }
  profile?: Omit<
    NonNullable<Child['profile']>,
    'familyPosition'
    | 'familyMaritalStatus'
    | 'familySpokenLanguage'
    | 'englishAsAdditionalLanguage'
    | 'specialEducationalNeeds'
  > & {
    englishAsAdditionalLanguage?: boolean
    englishAsAdditionalLanguageDetails?: string
    familyMaritalStatus?: Option<FamilyMaritalStatus>
    familyPosition?: Option<FamilyPosition>
    familySpokenLanguage?: Option[]
    specialEducationalNeeds?: boolean
    specialEducationalNeedsDetails?: string
  }
  religion?: Option<string>
}
export interface FormPreviewFormValues {
  data: FormPreviewDataFormValues
  signature?: SelectedImage
}

export interface RegistrationFormBodyCarerChildRelation {
  carer?: Carer
  objectAction?: RegistrationFormObjectAction
  objectId?: number
  relation?: string
}

export interface RegistrationFormBody {
  data: Omit<Child, 'id' | 'ethnicity' | 'gender' | 'religion'> & {
    carerChildRelations?: RegistrationFormBodyCarerChildRelation[]
    ethnicity?: {
      id: number
    }
    gender?: string
    religion?: string
  }
  signature?: Signature
  status: RegistrationFormStatus
}

export interface RegistrationForm {
  carer?: Carer
  child?: Child
  createdAt?: Date
  data?: Child
  fields?: any[]
  id?: number
  sentAt?: string
  signature?: {
    url?: string
  }
  status?: RegistrationFormStatus
  token?: string
  updatedAt?: Date
}

export const STATUS_LABELS = {
  [RegistrationFormStatus.NOT_SENT]: i18n.t('module:Children:Child:About:Profile:RegistrationForm:Statuses:notSend'),
  [RegistrationFormStatus.SENT]: i18n.t('global:Sent'),
  [RegistrationFormStatus.LINK_EXPIRED]: (
    i18n.t('module:Children:Child:About:Profile:RegistrationForm:Statuses:linkExpired')
  ),
  [RegistrationFormStatus.COMPLETED]: i18n.t('module:Children:Child:About:Profile:RegistrationForm:Statuses:completed'),
}

export const STATUS_BADGE_COLOR = {
  [RegistrationFormStatus.NOT_SENT]: NEUTRAL_COLOURS.GRAY,
  [RegistrationFormStatus.SENT]: FLAG_COLOURS.WARNING,
  [RegistrationFormStatus.LINK_EXPIRED]: FLAG_COLOURS.ERROR,
  [RegistrationFormStatus.COMPLETED]: FLAG_COLOURS.SUCCESS,
}
